import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const ALL_PAGES = [
  { to: "/terms-of-service", label: "Terms of Service" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/refund-policy", label: "Refund Policy" },
  { to: "/cancellation-policy", label: "Cancellation Policy" },
];

export default function LegalLayout({ code, title, lastUpdated, children }) {
  return (
    <main
      data-testid={`legal-page-${code.toLowerCase()}`}
      className="bg-sebyx-bg text-sebyx-text font-body min-h-screen"
    >
      {/* Top nav */}
      <header className="border-b border-sebyx-border">
        <div className="px-6 md:px-12 lg:px-24 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="flex items-center gap-3 group"
          >
            <ArrowLeft
              size={16}
              className="text-sebyx-accent group-hover:-translate-x-0.5 transition-transform"
            />
            <span className="block w-2 h-2 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight text-sebyx-text">
              Sebyx
            </span>
            <span className="font-mono text-[10px] text-sebyx-muted hidden sm:block">
              / Revenue Architecture
            </span>
          </Link>
          <a
            href="/#contact"
            data-testid="legal-cta-contact"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-sebyx-accent text-sebyx-bg font-semibold text-sm hover:bg-[#3FA097] transition-colors"
          >
            Contact <ArrowUpRight size={14} />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 pt-20 pb-12 border-b border-sebyx-border">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-8 h-px bg-sebyx-accent" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
              {code}
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]">
            {title}
          </h1>
          <p className="mt-6 font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
            Last updated · {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 md:px-12 lg:px-24 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <div className="sticky top-24">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted block mb-4">
                Legal Index
              </span>
              <ul className="space-y-2">
                {ALL_PAGES.map((p) => (
                  <li key={p.to}>
                    <Link
                      to={p.to}
                      className="font-mono text-[11px] tracking-[0.2em] uppercase text-sebyx-text/80 hover:text-sebyx-accent transition-colors block py-1"
                    >
                      {p.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
          <article
            data-testid="legal-content"
            className="lg:col-span-9 max-w-3xl prose-sebyx"
          >
            {children}
          </article>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-sebyx-border px-6 md:px-12 lg:px-24 py-10 text-sebyx-muted font-mono text-[10px] tracking-[0.2em] uppercase flex flex-col gap-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <span>© {new Date().getFullYear()} Sebyx · Revenue Architecture</span>
          <span>contact@sebyx.com · +91 91084 36667</span>
        </div>
        <div className="pt-3 border-t border-sebyx-border leading-relaxed">
          Sebyx is a brand operated by{" "}
          <span className="text-sebyx-text">Serio Healthcare</span> · GSTIN{" "}
          <span className="text-sebyx-text">29APZPP8620D1Z8</span>
        </div>
      </footer>
    </main>
  );
}

/**
 * Reusable elements legal pages can compose.
 */
export function Section({ id, title, children }) {
  return (
    <section
      id={id}
      data-testid={`legal-section-${id}`}
      className="mb-12 scroll-mt-24"
    >
      <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-sebyx-text mb-4 border-b border-sebyx-border pb-3">
        {title}
      </h2>
      <div className="space-y-4 text-sebyx-text/90 leading-relaxed text-base">
        {children}
      </div>
    </section>
  );
}

export function P({ children }) {
  return <p className="leading-relaxed">{children}</p>;
}

export function UL({ children }) {
  return (
    <ul className="space-y-2 pl-0">
      {children}
    </ul>
  );
}

export function LI({ children }) {
  return (
    <li className="flex items-start gap-3 leading-relaxed">
      <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-2.5 shrink-0" />
      <span className="flex-1">{children}</span>
    </li>
  );
}

export function Callout({ children }) {
  return (
    <div className="border-l-2 border-sebyx-accent bg-sebyx-surface/60 pl-5 py-4 my-6">
      <p className="text-sebyx-text/90 leading-relaxed">{children}</p>
    </div>
  );
}
